// New Client Experience Service
// This service ensures every new client starts with a clean slate across the entire platform

export const NewClientExperience = {
  // Initialize a new client with zero metrics across all components
  initializeNewClient: (userData) => {
    console.log('🎯 Initializing new client experience for:', userData?.user_metadata?.company_name || 'New Client');
    
    // Clear any existing data
    NewClientExperience.clearExistingData();
    
    // Set up fresh localStorage with zero metrics
    NewClientExperience.setupFreshLocalStorage(userData);
    
    // Initialize all platform metrics to zero
    NewClientExperience.initializeZeroMetrics();
    
    // Set up first-time user flags
    NewClientExperience.setupFirstTimeFlags();
    
    console.log('✅ New client experience initialized successfully');
  },

  // Clear any existing data that might interfere with new client experience
  clearExistingData: () => {
    const keysToClear = [
      'userStats',
      'dashboardData',
      'analyticsData',
      'contentData',
      'leadData',
      'campaignData',
      'calendarData',
      'achievements',
      'hasLoggedInBefore',
      'aiAgentIntroduced'
    ];
    
    keysToClear.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`🗑️ Cleared: ${key}`);
      }
    });
  },

  // Set up fresh localStorage with zero metrics
  setupFreshLocalStorage: (userData) => {
    // Ensure company name is set
    const companyName = userData?.user_metadata?.company_name || 
                       userData?.user_metadata?.company || 
                       'Your Company';
    
    localStorage.setItem('companyName', companyName);
    
    // Set up fresh user stats with zero metrics
    const freshUserStats = {
      totalLeads: 0,
      activeCampaigns: 0,
      totalRevenue: 0,
      conversionRate: 0,
      engagementRate: 0,
      followers: 0,
      reach: 0,
      impressions: 0,
      level: 1,
      experience: 0,
      nextLevel: 100,
      achievements: [
        { id: 1, name: 'First Lead', icon: '🎯', unlocked: false },
        { id: 2, name: 'Social Butterfly', icon: '🦋', unlocked: false },
        { id: 3, name: 'Revenue Master', icon: '💰', unlocked: false },
        { id: 4, name: 'AI Pioneer', icon: '🤖', unlocked: false }
      ]
    };
    
    localStorage.setItem('userStats', JSON.stringify(freshUserStats));
    
    // Set up fresh dashboard data
    const freshDashboardData = {
      metrics: {
        followers: 0,
        engagement: 0,
        reach: 0,
        impressions: 0,
      },
      recentPosts: [],
      trends: [
        { metric: 'Followers', change: 0, positive: true },
        { metric: 'Engagement', change: 0, positive: true },
        { metric: 'Reach', change: 0, positive: true },
      ],
    };
    
    localStorage.setItem('dashboardData', JSON.stringify(freshDashboardData));
    
    // Set up fresh analytics data
    const freshAnalyticsData = {
      totalRevenue: 0,
      totalLeads: 0,
      totalEngagement: 0,
      totalReach: 0,
      growthRate: 0,
      conversionRate: 0,
      avgOrderValue: 0,
      customerLifetimeValue: 0,
      roi: 0,
      costPerLead: 0,
      engagementRate: 0,
      viralCoefficient: 0,
      retentionRate: 0,
      churnRate: 0,
      platforms: [
        {
          name: 'Instagram',
          followers: 0,
          engagement: 0,
          reach: 0,
          revenue: 0,
          growth: 0,
          color: '#e4405f'
        },
        {
          name: 'Facebook',
          followers: 0,
          engagement: 0,
          reach: 0,
          revenue: 0,
          growth: 0,
          color: '#1877f2'
        },
        {
          name: 'TikTok',
          followers: 0,
          engagement: 0,
          reach: 0,
          revenue: 0,
          growth: 0,
          color: '#000000'
        }
      ],
      topPosts: [],
      insights: []
    };
    
    localStorage.setItem('analyticsData', JSON.stringify(freshAnalyticsData));
    
    // Set up fresh content data
    const freshContentData = {
      totalPosts: 0,
      viralPosts: 0,
      totalEngagement: 0,
      conversionRate: 0,
      posts: [],
      campaigns: []
    };
    
    localStorage.setItem('contentData', JSON.stringify(freshContentData));
    
    // Set up fresh lead data
    const freshLeadData = {
      totalLeads: 0,
      qualifiedLeads: 0,
      hotLeads: 0,
      leads: [],
      campaigns: []
    };
    
    localStorage.setItem('leadData', JSON.stringify(freshLeadData));
    
    // Set up fresh campaign data
    const freshCampaignData = {
      totalCampaigns: 0,
      activeCampaigns: 0,
      totalSpent: 0,
      totalRevenue: 0,
      campaigns: []
    };
    
    localStorage.setItem('campaignData', JSON.stringify(freshCampaignData));
    
    // Set up fresh calendar data
    const freshCalendarData = {
      totalScheduled: 0,
      totalPublished: 0,
      totalDrafts: 0,
      completionRate: 0,
      streakDays: 0,
      bestTimeSlots: [],
      scheduledPosts: []
    };
    
    localStorage.setItem('calendarData', JSON.stringify(freshCalendarData));
  },

  // Initialize all platform metrics to zero
  initializeZeroMetrics: () => {
    // This function ensures all components start with zero metrics
    // It's called during new client initialization
    
    // Note: Individual components will read from localStorage
    // and initialize their state with the zero values we set above
    
    console.log('📊 All platform metrics initialized to zero');
  },

  // Set up first-time user flags
  setupFirstTimeFlags: () => {
    // Clear any existing flags
    localStorage.removeItem('hasLoggedInBefore');
    localStorage.removeItem('aiAgentIntroduced');
    
    // Set up fresh onboarding progress
    const freshOnboardingProgress = {
      completedSteps: [],
      currentStep: 0,
      totalSteps: 5,
      isComplete: false
    };
    
    localStorage.setItem('onboardingProgress', JSON.stringify(freshOnboardingProgress));
    
    console.log('🎯 First-time user flags set up');
  },

  // Get fresh metrics for any component
  getFreshMetrics: (componentName) => {
    const metricsMap = {
      'dashboard': {
        totalLeads: 0,
        activeCampaigns: 0,
        totalRevenue: 0,
        conversionRate: 0,
        engagementRate: 0,
        followers: 0,
        reach: 0,
        impressions: 0,
        level: 1,
        experience: 0,
        nextLevel: 100
      },
      'analytics': {
        totalRevenue: 0,
        totalLeads: 0,
        totalEngagement: 0,
        totalReach: 0,
        growthRate: 0,
        conversionRate: 0,
        avgOrderValue: 0,
        customerLifetimeValue: 0,
        roi: 0,
        costPerLead: 0,
        engagementRate: 0,
        viralCoefficient: 0,
        retentionRate: 0,
        churnRate: 0
      },
      'content': {
        totalPosts: 0,
        viralPosts: 0,
        totalEngagement: 0,
        conversionRate: 0
      },
      'leads': {
        totalLeads: 0,
        qualifiedLeads: 0,
        hotLeads: 0
      },
      'campaigns': {
        totalCampaigns: 0,
        activeCampaigns: 0,
        totalSpent: 0,
        totalRevenue: 0
      },
      'calendar': {
        totalScheduled: 0,
        totalPublished: 0,
        totalDrafts: 0,
        completionRate: 0,
        streakDays: 0
      }
    };
    
    return metricsMap[componentName] || {};
  },

  // Check if this is a new client (first time login)
  isNewClient: () => {
    return !localStorage.getItem('hasLoggedInBefore');
  },

  // Mark client as no longer new
  markAsEstablishedClient: () => {
    localStorage.setItem('hasLoggedInBefore', 'true');
    console.log('✅ Client marked as established');
  },

  // Get welcome message for new client
  getWelcomeMessage: () => {
    const companyName = localStorage.getItem('companyName') || 'Your Company';
    
    return {
      title: `Welcome to Your Social Media Empire, ${companyName}!`,
      message: `You're all set up and ready to dominate social media! Your dashboard has been initialized with fresh metrics, and you're starting your journey at Level 1. Let's get you to your first million views!`,
      type: 'success'
    };
  },

  // Get AI agent introduction for new client
  getAIAgentIntroduction: () => {
    return `🤖 **Hello! I'm your AI Social Media Assistant!**

I'm here to help you build and grow your social media presence from the ground up. Here's what I can do for you:

🎯 **Content Creation & Strategy**
• Generate engaging posts and captions
• Create hashtag strategies
• Plan content calendars
• Optimize content for each platform

📊 **Campaign Management**
• Set up and optimize ad campaigns
• Track performance metrics
• A/B test different approaches
• Maximize your ROI

📈 **Analytics & Insights**
• Monitor your social media performance
• Identify trending topics
• Track competitor activity
• Provide growth recommendations

💡 **Lead Generation**
• Find potential customers
• Qualify leads automatically
• Create follow-up sequences
• Track conversion rates

I'm constantly learning and adapting to help you achieve your business goals. Just ask me anything about social media marketing, and I'll guide you through it!

What would you like to work on today?`;
  }
};

export default NewClientExperience;
